import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { api } from "../lib/api";
import BrandMark from "../components/BrandMark";

const SECTION_GROUPS = [
  { title: "Отрасль и продукт", sections: ["Отрасль и продукт"] },
  { title: "Стадия и продукт", sections: ["Стадия"] },
  { title: "Клиенты, выручка, бизнес-модель", sections: ["Клиенты и продажи", "Выручка и traction", "Бизнес-модель", "Экономика"] },
  { title: "Корпоративная применимость", sections: ["Корпоративная применимость"] },
  { title: "Институциональная поддержка и технологии", sections: ["Институциональная поддержка", "Технология и IP"] },
  { title: "Инвестиционная готовность и рынок", sections: ["Инвестиционная готовность", "Рынок"] },
  { title: "Команда и риски", sections: ["Команда", "Риски"] },
];

export default function Questionnaire() {
  const nav = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => {
    const saved = sessionStorage.getItem("srd_answers");
    return saved ? JSON.parse(saved) : {};
  });
  const [error, setError] = useState("");

  useEffect(() => {
    const type = sessionStorage.getItem("srd_questionnaire_type") || "mvp";
    api.get(`/api/questionnaire?type=${type}`).then((r) => setQuestionnaire(r.data));
  }, []);

  useEffect(() => {
    sessionStorage.setItem("srd_answers", JSON.stringify(answers));
  }, [answers]);

  const stepGroups = useMemo(() => {
    if (!questionnaire) return [];
    return SECTION_GROUPS.map((g) => ({
      ...g,
      questions: questionnaire.questions.filter((q) => g.sections.includes(q.section)),
    }));
  }, [questionnaire]);

  if (!questionnaire) {
    return <div className="min-h-screen flex items-center justify-center label-mono">Загрузка опросника…</div>;
  }
  const totalSteps = stepGroups.length;
  const currentGroup = stepGroups[step];

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const validateStep = () => {
    for (const q of currentGroup.questions) {
      if (!q.required) continue;
      const v = answers[q.id];
      if (q.type === "multiple_choice") {
        if (!Array.isArray(v) || v.length === 0) return q.id;
      } else {
        if (v === undefined || v === null || v === "") return q.id;
      }
    }
    return null;
  };

  const next = () => {
    const missing = validateStep();
    if (missing) {
      setError(`Не отвечен обязательный вопрос: ${missing}`);
      const el = document.querySelector(`[data-qid="${missing}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setError("");
    if (step + 1 < totalSteps) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      nav("/review");
    }
  };

  const back = () => {
    setError("");
    if (step === 0) nav("/founder");
    else { setStep(step - 1); window.scrollTo({ top: 0, behavior: "instant" }); }
  };

  const overallAnswered = Object.keys(answers).length;
  const overallTotal = questionnaire.questions.length;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="border-b border-black/10 sticky top-0 z-30 bg-background">
        <div className="max-w-screen-xl mx-auto px-8 py-4 flex items-center justify-between">
          <BrandMark />
          <div className="label-mono">
            <span data-testid="step-indicator">{String(step + 1).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}</span>
            {" · "}
            <span data-testid="answered-counter">{overallAnswered}/{overallTotal}</span>
          </div>
        </div>
        <div className="h-1 bg-muted">
          <div className="h-full bg-foreground transition-all" style={{ width: `${((step) / (totalSteps - 1 || 1)) * 100}%` }} />
        </div>
      </div>

      <main className="flex-1">
        <div className="max-w-screen-xl mx-auto px-8 py-12 md:py-16 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="label-mono">БЛОК {String(step + 1).padStart(2, "0")}</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-medium leading-tight">{currentGroup.title}</h2>
            <p className="mt-4 text-sm text-foreground/65 leading-relaxed">
              Отвечайте честно — результат зависит от точности данных. Открытые ответы помогают увидеть
              нюансы, которые не&nbsp;закрываются вариантами ответа.
            </p>
            <div className="mt-8 space-y-1">
              {stepGroups.map((g, i) => (
                <div key={i} className={`label-mono py-1 ${i === step ? "text-foreground" : "text-foreground/40"}`}>
                  {String(i + 1).padStart(2, "0")} · {g.title}
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-8 space-y-10">
            {currentGroup.questions.map((q) => (
              <QuestionView
                key={q.id}
                q={q}
                value={answers[q.id]}
                onChange={(v) => setAnswer(q.id, v)}
              />
            ))}
            {error && <p className="text-destructive label-mono" data-testid="form-error">{error}</p>}
            <div className="flex gap-4 pt-6 border-t border-black/10">
              <button className="btn-secondary" onClick={back} data-testid="step-back-btn">
                <ArrowLeft size={16} /> Назад
              </button>
              <button className="btn-primary" onClick={next} data-testid="step-next-btn">
                {step + 1 === totalSteps ? "К просмотру" : "Далее"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function QuestionView({ q, value, onChange }) {
  return (
    <section data-qid={q.id} className="grid-cell border-l-0 border-r-0 px-0 py-6">
      <div className="flex items-baseline gap-3">
        <div className="label-mono">{q.id.replace(/^q0?/, "Q")}</div>
        {q.required && <div className="label-mono text-destructive">REQUIRED</div>}
      </div>
      <h3 className="text-xl font-medium mt-3">{q.question}</h3>
      <div className="mt-5">
        {q.type === "single_choice" && (
          <div className="grid md:grid-cols-2 gap-3">
            {q.options.map((opt) => {
              const selected = value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`opt-${q.id}-${opt.value}`}
                  className={`option-card ${selected ? "selected" : ""}`}
                  onClick={() => onChange(opt.value)}
                >
                  <span className="radio-pin" />
                  <span className="text-sm">{opt.label}</span>
                  {selected && <Check size={14} className="ml-auto" />}
                </button>
              );
            })}
          </div>
        )}
        {q.type === "multiple_choice" && (
          <div className="grid md:grid-cols-2 gap-3">
            {q.options.map((opt) => {
              const arr = Array.isArray(value) ? value : [];
              const selected = arr.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`opt-${q.id}-${opt.value}`}
                  className={`option-card ${selected ? "selected" : ""}`}
                  onClick={() => onChange(selected ? arr.filter((x) => x !== opt.value) : [...arr, opt.value])}
                >
                  <span className="radio-pin" />
                  <span className="text-sm">{opt.label}</span>
                  {selected && <Check size={14} className="ml-auto" />}
                </button>
              );
            })}
          </div>
        )}
        {q.type === "long_text" && (
          <textarea
            className="field-input"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Опишите развёрнуто, на сколько считаете нужным"
            data-testid={`opt-${q.id}-text`}
          />
        )}
      </div>
    </section>
  );
}
